import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Icon } from '@bciti/types';

import { PermitCategoryService } from '../../services/permit-category.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { PermitCategory, CreateCategoryPayload } from '../../models/permit-category.model';

@Component({
    selector: 'app-permit-category-form',
    templateUrl: './permit-category-form.component.html',
    styleUrls: ['./permit-category-form.component.scss'],
})
export class PermitCategoryFormComponent implements OnInit, OnDestroy, OnChanges {
    @Input() categoryToEdit: PermitCategory | null = null;

    @Output() saved = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    form: FormGroup;
    isSaving = false;

    // Full bciti icon set for the visual picker (slugs like "icon-folder").
    readonly iconOptions: string[] = Object.values(Icon);

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private categoryService: PermitCategoryService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.initForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.categoryToEdit && this.form) {
            this.patchForm();
        }
    }

    private initForm(): void {
        const c = this.categoryToEdit;
        this.form = this.fb.group({
            nameFr: [c?.name?.fr || '', Validators.required],
            nameEn: [c?.name?.en || '', Validators.required],
            icon: [c?.icon || ''],
            active: [c ? c.active : true],
        });
    }

    private patchForm(): void {
        const c = this.categoryToEdit;
        this.form.patchValue({
            nameFr: c?.name?.fr || '',
            nameEn: c?.name?.en || '',
            icon: c?.icon || '',
            active: c ? c.active : true,
        });
    }

    get isEdit(): boolean {
        return !!this.categoryToEdit;
    }

    onSave(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.isSaving) return;

        this.isSaving = true;
        const v = this.form.value;
        const payload: CreateCategoryPayload = {
            name: { fr: v.nameFr.trim(), en: v.nameEn.trim() },
            active: v.active,
        };
        if (v.icon && v.icon.trim()) {
            payload.icon = v.icon.trim();
        }

        const request$ = this.categoryToEdit ? this.categoryService.updateCategory(this.categoryToEdit._id, payload) : this.categoryService.createCategory(payload);

        request$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.isSaving = false;
                const key = this.isEdit ? 'permit.config.categories.form_saved_edit' : 'permit.config.categories.form_saved_create';
                this.messageService.success(this.translate.instant(key));
                this.saved.emit();
            },
            error: () => {
                this.isSaving = false;
                this.messageService.error(this.translate.instant('permit.config.common.error'));
            },
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
